import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';

import { Button, Chip, CircularProgress, Box } from '@mui/material';
import { AttachFile, SaveAlt, Close } from '@mui/icons-material';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';

import theme from 'theme';
import { doc, getDoc } from 'firebase/firestore';
import { apiClient } from 'api-client';
import { useAuth } from 'auth';
import { useFirestore, useStorage } from 'utils/firebase';
import { INVOICE_MAX_FILE_SIZE_BYTES, ALLOWED_INVOICE_MIME_TYPES } from 'constants/fieldLimits';

const InvoiceUpload = props => {
  const { enqueueSnackbar } = useSnackbar();
  const { budgetCostName, eduBudgetRequestId, status } = props;

  const storage = useStorage();
  const database = useFirestore();
  const { isHardwareManager, getToken } = useAuth();

  const canManageInvoices = !isHardwareManager;
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const inputRef = useRef(null);

  const getUploadedInvoices = useCallback(async () => {
    const eduBudgetRequestRef = doc(database, 'edu_budget_requests', eduBudgetRequestId);
    const eduBudgetRequestSnap = await getDoc(eduBudgetRequestRef);

    const eduBudgetRequest = eduBudgetRequestSnap.data();

    const { budgetCosts } = eduBudgetRequest;
    const budgetCost = budgetCosts.find(item => item.budgetCost === budgetCostName);
    if (!budgetCost) return [];
    return budgetCost.invoiceIds;
  }, [budgetCostName, database, eduBudgetRequestId]);

  useEffect(() => {
    const fetchData = async () => {
      const invoiceIds = await getUploadedInvoices();
      setFiles(invoiceIds);
    };
    fetchData();
  }, [getUploadedInvoices]);

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  const updateBudgetCostInvoiceIds = async invoiceId => {
    setFiles(files.concat([invoiceId]));

    const result = await apiClient(
      'POST',
      `/requests/edu-budget/${eduBudgetRequestId}/invoices`,
      getToken,
      { budgetCost: budgetCostName, invoiceId },
    );
    if (result?.status !== 'OK') {
      throw new Error(result?.message || 'Request failed');
    }
  };

  const checkIfFileExists = async invoiceId => {
    const eduBudgetRequestRef = doc(database, 'edu_budget_requests', eduBudgetRequestId);
    const eduBudgetRequestSnap = await getDoc(eduBudgetRequestRef);

    const eduBudgetRequest = eduBudgetRequestSnap.data();

    const { budgetCosts } = eduBudgetRequest;

    let foundCost = false;

    budgetCosts.forEach(cost => {
      if (cost.invoiceIds.includes(invoiceId)) foundCost = true;
    });

    return foundCost;
  };

  const handleFileChange = async e => {
    if (!e.target.files) {
      return;
    }
    const currentFile = e.target.files[0];

    if (!ALLOWED_INVOICE_MIME_TYPES.includes(currentFile.type)) {
      enqueueSnackbar('File type not supported. Please upload a PDF or image (JPEG, PNG, WEBP).', {
        variant: 'error',
      });
      e.target.value = null;
      return;
    }

    if (currentFile.size > INVOICE_MAX_FILE_SIZE_BYTES) {
      enqueueSnackbar(
        `File is too large. Maximum allowed size is ${INVOICE_MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB.`,
        { variant: 'error' },
      );
      e.target.value = null;
      return;
    }

    setLoading(true);
    try {
      let invoiceId = `invoices/${eduBudgetRequestId}/${currentFile.name}`;

      const exists = await checkIfFileExists(invoiceId);
      if (exists) {
        const splitName = currentFile.name.split('.');
        invoiceId = `invoices/${eduBudgetRequestId}/${splitName[0]}_${Date.now()}.${splitName[1]}`;
      }

      const invoiceRef = ref(storage, invoiceId);
      await uploadBytes(invoiceRef, currentFile);
      await updateBudgetCostInvoiceIds(invoiceId);
    } catch (error) {
      console.error('Error uploading the invoice', error);
      enqueueSnackbar('Error uploading the invoice. Please try again.', { variant: 'error' });
    } finally {
      setLoading(false);
      e.target.value = null;
    }
  };

  const deleteBudgetCostInvoiceIds = async invoiceId => {
    setFiles(files.filter(file => file !== invoiceId));

    const result = await apiClient(
      'DELETE',
      `/requests/edu-budget/${eduBudgetRequestId}/invoices`,
      getToken,
      { budgetCost: budgetCostName, invoiceId },
    );
    if (result?.status !== 'OK') {
      throw new Error(result?.message || 'Request failed');
    }
  };

  const downloadInvoice = async invoiceId => {
    const invoiceRef = ref(storage, invoiceId);
    try {
      const url = await getDownloadURL(invoiceRef);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const element = document.createElement('a');
      element.href = blobUrl;
      element.download = invoiceId.split('/').pop();

      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);

      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading the file', error);
      enqueueSnackbar('Error downloading the file', {
        variant: 'error',
      });
    }
  };

  const previewInvoice = async invoiceId => {
    const invoiceRef = ref(storage, invoiceId);
    await getDownloadURL(invoiceRef)
      .then(url => {
        window.open(url, '_blank', 'noopener,noreferrer');
      })
      .catch(error => {
        console.error('Error previewing the file', error);
        enqueueSnackbar('Error previewing the file', {
          variant: 'error',
        });
      });
  };

  const deleteInvoice = async invoiceId => {
    try {
      await deleteBudgetCostInvoiceIds(invoiceId);
    } catch (error) {
      console.error('Error deleting the invoice', error);
      enqueueSnackbar('Error deleting the invoice', {
        variant: 'error',
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      {files.length !== 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {files.map(file => (
            <Box
              key={file}
              sx={{
                display: 'flex',
                '& .MuiChip-root': {
                  backgroundColor: '#F7F7F9',
                },
                '&:not(:last-child)': {
                  marginBottom: '0.5rem',
                },
              }}
            >
              <Chip
                label={file.split('/')[2]}
                sx={{
                  width: '12.8rem',
                  height: '2rem',
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: 'black',
                  marginRight: '0.5rem',
                }}
                onDelete={
                  status === 'approved' && canManageInvoices ? () => deleteInvoice(file) : undefined
                }
                deleteIcon={
                  <Close sx={{ color: 'black', width: '1rem' }} data-cy="deleteInvoiceButton" />
                }
                onClick={() => previewInvoice(file)}
              />
              <Button
                sx={{
                  minWidth: '2rem',
                  height: '2rem',
                  padding: 0,
                  background: '#F7F7F9',
                  color: theme.palette.primary.main,
                  marginRight: '1rem',
                }}
                type="button"
                data-cy="downloadInvoiceButton"
                onClick={() => downloadInvoice(file)}
              >
                <SaveAlt sx={{ height: '1rem' }} fontSize="small" />
              </Button>
            </Box>
          ))}
        </Box>
      )}

      {status === 'approved' && canManageInvoices ? (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            sx={{
              minWidth: '2rem',
              height: '2rem',
              padding: 0,
              background: '#F7F7F9',
              color: theme.palette.primary.main,
              marginRight: files.length === 0 ? '1rem' : 0,
            }}
            type="button"
            onClick={handleUploadClick}
          >
            {
              // TODO use LoadingButton component after material-ui update
              loading ? (
                <CircularProgress color="secondary" size={15} aria-label="Uploading invoice" />
              ) : (
                <AttachFile sx={{ height: '1rem' }} fontSize="small" />
              )
            }
          </Button>
          {files.length === 0 && <div>Upload your file</div>}
        </Box>
      ) : (
        <div />
      )}

      <input
        type="file"
        ref={inputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept={ALLOWED_INVOICE_MIME_TYPES.join(',')}
        data-cy="invoiceFileInput"
      />
    </Box>
  );
};

export default InvoiceUpload;
